/**
 * ResearchResponseView - the intent dispatcher.
 *
 * Takes a `ResearchResponse` envelope (built by `./adapter`) and picks the
 * renderer for its `intent`.
 *
 * memo / deep_research keep the memo visual (`ResearchResultRenderer`);
 * every other intent has its own typed renderer. `MarkdownFallback` remains
 * for unknown intents, malformed payloads, and the error boundary.
 *
 * Any intent whose payload is missing or malformed (`meta.payloadValid` is
 * false) degrades to markdown rather than erroring, and a render crash inside
 * a typed renderer is caught and degraded the same way.
 */

import React, { Component, ErrorInfo, useEffect } from 'react';
import ResearchResultRenderer from '../Chat/ResearchResultRenderer';
import { chatContentStyles } from '../Chat/MarkdownStream';
import MarkdownFallback from './MarkdownFallback';
import CitationReferences from './renderers/CitationReferences';
import { memoPayloadOf } from './adapter';
import BulletList from './renderers/BulletList';
import ChronologyTimeline from './renderers/ChronologyTimeline';
import DraftDocument from './renderers/DraftDocument';
import ProseAnswer from './renderers/ProseAnswer';
import RankedSourceList from './renderers/RankedSourceList';
import {
  BulletsPayload,
  ChronologyPayload,
  CitationOpenRequest,
  DraftPayload,
  ProsePayload,
  ResearchResponse,
  SourceListPayload,
} from './types';

/* ── Error boundary so a typed renderer crash never kills the page ── */
class ResearchRenderErrorBoundary extends Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ResearchResponseView] Render error:', error, info);
  }
  render() {
    return this.state.hasError ? <>{this.props.fallback}</> : <>{this.props.children}</>;
  }
}

export interface ResearchResponseViewProps {
  response: ResearchResponse;
  /** Live (streaming) responses reveal paragraph by paragraph. */
  isLive?: boolean;
  onComplete?: () => void;
  onOpenCitation: (url: string) => void;
  /** Richer citation-open contract - carries the highlight range when trustworthy. */
  onOpenCitationRequest?: (request: CitationOpenRequest) => void;
}

const NOOP = () => {};

const ResearchResponseView: React.FC<ResearchResponseViewProps> = ({
  response,
  isLive = false,
  onComplete = NOOP,
  onOpenCitation,
  onOpenCitationRequest,
}) => {
  const memo = memoPayloadOf(response);
  const { citations, meta } = response;
  const handlers = { onOpenCitation, onOpenCitationRequest };

  // Structured renderers have nothing to stream - signal completion at once.
  const isTyped = meta.payloadValid;
  useEffect(() => {
    if (isTyped) onComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTyped, response]);

  const fallback = (
    <>
      <MarkdownFallback
        response={response}
        isLive={isLive}
        onComplete={onComplete}
        onOpenCitation={onOpenCitation}
      />
      <CitationReferences citations={citations} {...handlers} />
    </>
  );

  /**
   * Typed renderers degrade to markdown on a render crash, and EVERY answer
   * carries its references beneath it - the list is the answer's evidence, so
   * it does not belong to any one renderer.
   */
  const guarded = (children: React.ReactNode) => (
    <ResearchRenderErrorBoundary fallback={fallback}>
      {children}
      <CitationReferences citations={citations} {...handlers} />
    </ResearchRenderErrorBoundary>
  );

  switch (response.intent) {
    case 'memo':
    case 'deep_research':
      if (!memo) return fallback;
      return (
        <>
          <style>{chatContentStyles}</style>
          {guarded(<ResearchResultRenderer data={memo} onOpenCitation={onOpenCitation} />)}
        </>
      );

    case 'qa':
    case 'overview':
      if (!meta.payloadValid) return fallback;
      return guarded(
        <ProseAnswer
          payload={response.payload as ProsePayload}
          citations={citations}
          {...handlers}
        />
      );

    case 'chronology':
      if (!meta.payloadValid) return fallback;
      return guarded(
        <ChronologyTimeline
          payload={response.payload as ChronologyPayload}
          citations={citations}
          {...handlers}
        />
      );

    case 'bullets':
      if (!meta.payloadValid) return fallback;
      return guarded(
        <BulletList
          payload={response.payload as BulletsPayload}
          citations={citations}
          {...handlers}
        />
      );

    case 'find_document':
      if (!meta.payloadValid) return fallback;
      return guarded(
        <RankedSourceList
          payload={response.payload as SourceListPayload}
          citations={citations}
          {...handlers}
        />
      );

    case 'draft':
      if (!meta.payloadValid) return fallback;
      return guarded(
        <DraftDocument
          payload={response.payload as DraftPayload}
          citations={citations}
          markdown={response.markdown}
          review={meta.draftReview}
          verification={meta.verification}
          {...handlers}
        />
      );

    default:
      return fallback;
  }
};

export default ResearchResponseView;
